import { AddOutlined, ContentCopy, DeleteOutlineOutlined, Edit, Power, PowerOff } from "@mui/icons-material";
import { Box, Button, FormControlLabel, IconButton, Switch } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";



import { useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { useSysDataManagerClubsQuery } from "@/api/hooks/sys-data-hooks";
import { useCfgPerm, useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import ApplyAuthGroupsTableControl from "@/components/apply-auth-groups-table-control";
import BulkOperationReportModal from "@/components/bulk-operation-report-modal";
import Popconfirm from "@/components/popcofirm";
import { useTableSettings } from "@/hooks/use-table-settings";
import { assembleNamesFilter } from "@/lib/cfg-utils";
import { ROOM_CONFIGS_NAMES } from "@/lib/constants";
import { useConfigChangeEnabled, useDeleteConfigs, useSaveConfig, useUpdateConfig } from "@/module/room-configs-module/hooks/room-configs-hooks";
import { useManagerConfigTableColumns } from "@/module/room-configs-module/manager-config-module/manager-config-table-columns";
import { ClubManagerModal } from "@/module/room-configs-module/manager-config-module/manager-create-modal";
import { ClubManager, EnrichedClub, enrichClubManagerNewWithClubData } from "@/module/room-configs-module/manager-config-module/utils";
import { ResultDtoLong } from "@/shared/api";


const ManagerConfigTable = ({ room }: { room: string }) => {
  const { t } = useTranslation();
  const {
    data: configsRs,
    isLoading: configsIsLoading,
    refetch,
  } = useSysCfgQuery({
    domain: room,
    config_name: ROOM_CONFIGS_NAMES.MANAGER,
  });

  const { data: clubConfigsData } = useSysDataManagerClubsQuery({
    domain: room,
  });

  const cfgPerm = useCfgPerm({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.CMS_AGGREGATOR, //TODO to change
  });

  const isOwner = useIsOwnerOrSuperAdmin();

  const roomConfigsSortedData = React.useMemo(() => {
    const data = enrichClubManagerNewWithClubData(
      configsRs?.data.content ?? [],
      clubConfigsData?.data ?? []
    );
    return (
      data.sort(
        (a, b) => new Date(b.created_at).getDate() - new Date(a.created_at).getDate()
      ) ?? []
    );
  }, [clubConfigsData, configsRs]);

  const [bulkOperationResult, setBulkOperationResult] = useState<ResultDtoLong[] | undefined>(
    undefined
  );


  const {saveConfigMutation: handleClickSaveNewConfig, isPending: isSaveConfigPending} = useSaveConfig({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.MANAGER,
    onSuccessCallback: () => {
      void refetch();
      setModalInitValues(undefined);
      setModalMode(undefined);
      setEditItemName("");
    },
  });

  const {updateConfigMutation: handleClickUpdateConfig, isPending: isUpdateConfigPending} = useUpdateConfig({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.MANAGER,
    onSuccessCallback: () => {
      void refetch();
      setModalInitValues(undefined);
      setModalMode(undefined);
      setEditItemName("");
    },
  });

  const handleChangeEnableBulk = useConfigChangeEnabled({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.MANAGER,
    onSuccessCallback: (table) => {
      void refetch();
      table.resetRowSelection();
    },
  });

  const handleDeleteConfigs = useDeleteConfigs({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.MANAGER,
    onSuccessCallback: (table, response) => {
      setBulkOperationResult([response.data]);
      void refetch();
      table.resetRowSelection();
    },
  });

  const [modalMode, setModalMode] = useState<"edit" | "add" | undefined>(undefined);
  const [modalInitValues, setModalInitValues] = useState<ClubManager | undefined>(undefined);
  const [editItemName, setEditItemName] = useState<string>("");
  
  //This logic for reset row selection while using filtering/grouping

  const [enableColumnDragging, setEnableColumnDragging] = useState<boolean>(false);
  const [enableColumnSorting, setEnableColumnSorting] = useState<boolean>(false);

  const { defaultProps, state, columnFilters, grouping, globalFilter } =
    useTableSettings<EnrichedClub>("manager");

  const columns = useManagerConfigTableColumns()

  const table = useMaterialReactTable({
    data: roomConfigsSortedData ?? [],
    columns: columns,
    getRowId: (originalRow) => originalRow.id,
    selectAllMode: "all",
    state: {
      isLoading: configsIsLoading,
      ...state,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "name", desc: false }],
    },
    ...defaultProps,
    initialState: {
      pagination: { pageSize: 100, pageIndex: 0 },
      density: "compact",
      sorting: [{ id: "name", desc: false }],
    },
    enableRowActions: true,
    enableSelectAll: true,
    enableRowSelection: true,
    enableGrouping: true,
    enableColumnDragging: enableColumnDragging,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    enablePagination: true,
    enableSorting: enableColumnSorting,
    muiTableHeadCellProps: {
      sx: {
        verticalAlign: "middle",
        "& *": {
          whiteSpace: "normal",
          lineHeight: 1.35,
        },
      },
    },
    defaultColumn: {
      enableResizing: false,
      grow: false,
      maxSize: 30,
      minSize: 10,
    },

    renderTopToolbarCustomActions: ({ table }) => {
      const selectedCount = table.getSelectedRowModel().rows.length;

      return (
        <div style={{ height: "40px", display: "flex", alignItems: "center" }}>
          {cfgPerm.write && (
            <Button onClick={() => {
              setModalInitValues(undefined);
              setModalMode("add");
            }}>
              <AddOutlined /> {t("new")}
            </Button>
          )}

          {selectedCount > 0 && (
            <div style={{ marginLeft: 30, display: "flex" }}> 
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="success"
                  onClick={() => handleChangeEnableBulk(table, true)}
                >
                  <Power /> {t("enable")}
                </IconButton>
              )}
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="error"
                  onClick={() => handleChangeEnableBulk(table, false)}
                >
                  <PowerOff /> {t("disable")}
                </IconButton>
              )}

              {cfgPerm.delete && (
                <Popconfirm
                  title={t("deleteItems")}
                  description={t("areYouSureToDeleteItems")}
                  onConfirm={() => handleDeleteConfigs(table)}
                >
                  <IconButton size="small" style={{ fontSize: "15px" }} color="error">
                    <DeleteOutlineOutlined /> {t("delete")}
                  </IconButton>
                </Popconfirm>
              )}

              {isOwner && (
                <ApplyAuthGroupsTableControl
                  domain={room}
                  cfgName={ROOM_CONFIGS_NAMES.CMS_AGGREGATOR}
                  table={table}
                  filters={assembleNamesFilter(table)}
                  totalElementsCount={selectedCount}
                  onSuccess={() => void refetch()}
                />
              )}
            </div>
          )}
        </div>
      );
    },
    renderRowActions: ({ row }) => (
      <Box>
        <IconButton
          onClick={() => {
            const managerClubData = configsRs?.data.content.find((item) => item.id === row.original.id) as unknown as ClubManager;

            setModalInitValues({
              ...managerClubData,
              auth_groups: row.original.auth_groups ?? new Set(),
            })
            setEditItemName(managerClubData.name)
            setModalMode("edit");
          }}
          >
          <Edit />
        </IconButton>

        {cfgPerm.write && (
          <IconButton
            onClick={() => {
              const managerClubData = configsRs?.data.content.find((item) => item.id === row.original.id) as unknown as ClubManager;

              setModalInitValues({
                ...managerClubData,
                name: "",
                auth_groups: new Set()
              });
              setModalMode("add")
            }}
          >
            <ContentCopy />
          </IconButton>
        )}
      </Box>
    ),
    renderBottomToolbarCustomActions: () => (
      <>
        <FormControlLabel
          value={enableColumnDragging}
          control={
            <Switch
              size="small"
              onClick={() => setEnableColumnDragging(!enableColumnDragging)}
            />
          }
          label={t("columnsDragging")}
          labelPlacement="start"
        />
        <FormControlLabel
          value={enableColumnSorting}
          control={
            <Switch
              size="small"
              onClick={() => setEnableColumnSorting(!enableColumnSorting)}
            />
          }
          label={t("columnsSorting")}
          labelPlacement="start"
        />
      </>
    ),
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [columnFilters, grouping, globalFilter, table]);

  return (
    <>
      <MaterialReactTable table={table} />

      {modalMode && (
        <ClubManagerModal
          open={!!modalMode}
          mode={modalMode}
          initialData={modalInitValues}
          onSave={(data) => {
            if (modalMode === "add") return handleClickSaveNewConfig(data);
            if (modalMode === "edit") return handleClickUpdateConfig(editItemName, data);
          }}
          onCancel={() => {
            setModalMode(undefined);
            setModalInitValues(undefined);
            setEditItemName("");
          }}
        />
      )}

      {bulkOperationResult && (
        <BulkOperationReportModal
          resultList={bulkOperationResult}
          title={t("operationResult")}
          showProblematic
          showSuccess
          onCancel={() => setBulkOperationResult(undefined)}
          open={!!bulkOperationResult}
        />
      )}
    </>
  );
};

export default ManagerConfigTable;
